/**
 * Plantillas de carga masiva: descargar un .xlsx con los encabezados esperados y
 * leer de vuelta lo que el usuario sube (.xlsx o .csv).
 *
 * Usa ExcelJS, igual que `exportExcel.js` (SheetJS se sacó del frontend). El .xls
 * viejo (binario) no se puede leer: se pide guardarlo como .xlsx o .csv.
 *
 * Las columnas se reconocen por el encabezado, sin importar mayúsculas, tildes ni
 * el orden, para que funcione también con una planilla propia que traiga esos
 * nombres.
 */

const AZUL = 'FF0047BB';

const normalizar = (s) => String(s ?? '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/\(.*?\)/g, '')
  .toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * @param {object} p
 * @param {string} p.archivo  nombre sin extensión
 * @param {Array<{header, key, ancho?, ejemplo?, nota?}>} p.columnas
 * @param {string[]} [p.instrucciones] líneas para la hoja "Instrucciones"
 */
export const descargarPlantilla = async ({ archivo, columnas, instrucciones = [] }) => {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Panel de administración · Talleres Ñuñoa';

  const hoja = wb.addWorksheet('Datos', { views: [{ state: 'frozen', ySplit: 1 }] });
  hoja.columns = columnas.map(c => ({ header: c.header, key: c.key, width: c.ancho || 20 }));
  const head = hoja.getRow(1);
  head.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  head.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL } };
  head.height = 22;
  columnas.forEach((c, i) => { if (c.nota) head.getCell(i + 1).note = c.nota; });
  // Fila de ejemplo en gris: se recomienda borrarla, pero si queda se lee igual.
  const ejemplo = hoja.addRow(Object.fromEntries(columnas.map(c => [c.key, c.ejemplo ?? ''])));
  ejemplo.font = { italic: true, color: { argb: 'FF8C8C8C' } };
  // Todo como texto: que Excel no convierta "1" en número ni "16.858.694-9" en otra cosa.
  columnas.forEach((_, i) => { hoja.getColumn(i + 1).numFmt = '@'; });

  if (instrucciones.length) {
    const ins = wb.addWorksheet('Instrucciones');
    ins.getColumn(1).width = 110;
    instrucciones.forEach((linea, i) => {
      const r = ins.addRow([linea]);
      if (i === 0) r.font = { bold: true, size: 13 };
    });
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${archivo}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const valorCelda = (cell) => {
  const v = cell.value;
  if (v === null || v === undefined) return '';
  if (v instanceof Date) {
    // ExcelJS lee las fechas como UTC medianoche: se devuelve dd/mm/aaaa para que
    // no se corra un día al pasar por la zona horaria de Chile.
    const d = String(v.getUTCDate()).padStart(2, '0');
    const m = String(v.getUTCMonth() + 1).padStart(2, '0');
    return `${d}/${m}/${v.getUTCFullYear()}`;
  }
  return String(cell.text ?? '').trim();
};

const leerCsv = (texto) => {
  const lineas = texto.replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim() !== '');
  if (!lineas.length) return [];
  const sep = (lineas[0].match(/;/g) || []).length >= (lineas[0].match(/,/g) || []).length ? ';' : ',';
  const partir = (l) => {
    const out = []; let actual = ''; let comillas = false;
    for (const ch of l) {
      if (ch === '"') comillas = !comillas;
      else if (ch === sep && !comillas) { out.push(actual); actual = ''; }
      else actual += ch;
    }
    out.push(actual);
    return out.map(s => s.trim());
  };
  return lineas.map(partir);
};

/**
 * Lee el archivo subido y devuelve las filas como objetos con las `key` de las
 * columnas. Se salta la fila de ejemplo de la plantilla y las filas vacías.
 * @returns {Promise<{filas: object[], faltantes: string[]}>}
 */
export const leerPlanilla = async (file, columnas) => {
  const nombre = file.name.toLowerCase();
  let matriz;
  if (nombre.endsWith('.csv')) {
    matriz = leerCsv(await file.text());
  } else if (nombre.endsWith('.xlsx')) {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await file.arrayBuffer());
    const hoja = wb.getWorksheet('Datos') || wb.worksheets[0];
    matriz = [];
    hoja.eachRow({ includeEmpty: false }, (row) => {
      const fila = [];
      for (let i = 1; i <= hoja.columnCount; i++) fila.push(valorCelda(row.getCell(i)));
      matriz.push(fila);
    });
  } else {
    throw new Error('Formato no soportado: sube un .xlsx o un .csv (si es .xls, guárdalo como .xlsx)');
  }
  if (!matriz.length) return { filas: [], faltantes: columnas.filter(c => c.requerida).map(c => c.header) };

  const encabezado = matriz[0].map(normalizar);
  const indice = Object.fromEntries(columnas.map(c => {
    const alias = [c.header, c.key, ...(c.alias || [])].map(normalizar);
    return [c.key, encabezado.findIndex(h => alias.includes(h))];
  }));
  const faltantes = columnas.filter(c => c.requerida && indice[c.key] < 0).map(c => c.header);

  const ejemplo = columnas.map(c => String(c.ejemplo ?? ''));
  const filas = matriz.slice(1)
    .map(fila => Object.fromEntries(columnas.map(c => [c.key, indice[c.key] >= 0 ? (fila[indice[c.key]] ?? '') : ''])))
    .filter(f => Object.values(f).some(v => String(v).trim() !== ''))
    .filter(f => !columnas.every((c, i) => String(f[c.key]) === ejemplo[i]));
  return { filas, faltantes };
};
